import { workflowDetailSchema } from '@/features/rpa/recorder/lib/workflow-data-schema';
import type { WorkflowDetail } from '@/features/rpa/recorder/types/workflow';
import type { WorkflowWirePayload } from '@/tauri/rpa-recorder/upload-types';

export type ParseWorkflowFailureReason = 'missing_payload' | 'invalid_payload';

export type ParseWorkflowResult =
  | { readonly ok: true; readonly detail: WorkflowDetail }
  | { readonly ok: false; readonly reason: ParseWorkflowFailureReason };

const EMPTY_WORKFLOW_DETAIL: WorkflowDetail = {
  name: undefined,
  steps: [],
  parameters: [],
};

/** Best-effort parse for overlay skill helpers; returns empty detail on failure. */
export function parseWorkflowDetail(raw?: WorkflowWirePayload | null): WorkflowDetail {
  const result = parseWorkflow(raw);
  return result.ok ? result.detail : EMPTY_WORKFLOW_DETAIL;
}

export function parseWorkflow(raw?: WorkflowWirePayload | null): ParseWorkflowResult {
  if (raw == null) {
    return { ok: false, reason: 'missing_payload' };
  }

  const value = parseJsonValue(raw);
  if (!isObject(value)) {
    logParseFailure('invalid_payload');
    return { ok: false, reason: 'invalid_payload' };
  }

  const parsed = workflowDetailSchema.safeParse(value);
  if (!parsed.success) {
    logParseFailure('invalid_payload');
    return { ok: false, reason: 'invalid_payload' };
  }

  return { ok: true, detail: parsed.data };
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseJsonValue(raw: WorkflowWirePayload): unknown {
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as unknown;
    } catch {
      return raw;
    }
  }
  return raw;
}

function logParseFailure(reason: ParseWorkflowFailureReason): void {
  if (process.env.NODE_ENV !== 'development') return;
  console.debug('[parseWorkflow] failed to parse workflow payload', reason);
}
